// components/BluetoothConnect.tsx
import React, { useEffect, useRef, useState } from 'react';
import './BluetoothConnect.css';

// ---- 최소 타입 선언: TS에서 Web Bluetooth 타입이 없을 때 에러 방지 ----
declare global {
  interface Navigator {
    bluetooth?: any;
  }
}
type BluetoothDevice = any;
type BluetoothRemoteGATTServer = any;
type BluetoothRemoteGATTCharacteristic = any;
// -------------------------------------------------------------------------

const SERVICE_UUID = '4fafc201-1fb5-459e-8fcc-c5c9c331914b';
const CHARACTERISTIC_UUID = 'beb5483e-36e1-4688-b7f5-ea07361b26a8';

const BluetoothConnect: React.FC = () => {
  const [supported, setSupported] = useState<boolean>(false);
  const [device, setDevice] = useState<BluetoothDevice | null>(null);
  const [server, setServer] = useState<BluetoothRemoteGATTServer | null>(null);
  const [characteristic, setCharacteristic] = useState<BluetoothRemoteGATTCharacteristic | null>(null);
  const [status, setStatus] = useState<string>('대기 중');

  // Wi-Fi/MQTT 전송값
  const ssidRef = useRef<HTMLInputElement>(null);
  const pwRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const brokerRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setSupported(!!navigator.bluetooth);
  }, []);

  const connect = async () => {
    if (!navigator.bluetooth) {
      setSupported(false);
      return;
    }
    try {
      setStatus('기기 검색 중…');
      const dev: BluetoothDevice = await navigator.bluetooth.requestDevice({
        filters: [{ namePrefix: 'i2r' }],
        optionalServices: [SERVICE_UUID],
      });
      setDevice(dev);

      dev.addEventListener?.('gattserverdisconnected', handleDisconnected);

      setStatus('연결 중…');
      const gatt: BluetoothRemoteGATTServer | undefined = await dev.gatt?.connect();
      if (!gatt) throw new Error('GATT 연결 실패');
      setServer(gatt);

      const service = await gatt.getPrimaryService(SERVICE_UUID);
      const ch = await service.getCharacteristic(CHARACTERISTIC_UUID);
      setCharacteristic(ch);
      setStatus('연결됨');
    } catch {
      setStatus('에러');
    }
  };

  const handleDisconnected = () => {
    setStatus('연결 끊김');
    setServer(null);
    setCharacteristic(null);
  };

  const disconnect = async () => {
    try {
      device?.gatt?.disconnect();
      setStatus('연결 해제');
    } catch {
      // ignore
    }
  };

  // JSON 포맷으로 SSID/PW/EMAIL/BROKER 전송
  const sendWifiConfig = async () => {
    if (!characteristic) {
      return;
    }
    const payload = {
      c: 'si',
      ssid: ssidRef.current?.value || '',
      password: pwRef.current?.value || '',
      e: emailRef.current?.value || '',
      mqttBroker: brokerRef.current?.value || 'i2r',
    };
    try {
      const enc = new TextEncoder().encode(JSON.stringify(payload));
      await characteristic.writeValue(enc);
    } catch {
      // ignore
    }
  };

  return (
    <div className="bt-wrap">
      <h2 className="bt-title">기기연결 (Bluetooth)</h2>

      {/* 안내문 추가 */}
      <p className="bt-info">
        블루투스 모듈이 있어야 하며, 크롬 브라우저를 사용하세요.<br />
        안드로이드와 맥북은 동작하지만 아이폰은 연결되지 않습니다.
      </p>

      {!supported && (
        <p className="bt-warn">
          이 브라우저는 Web Bluetooth를 지원하지 않습니다. (HTTPS, Chrome/Edge/Android 권장)
        </p>
      )}

      <div className="bt-status"><b>상태:</b> {status}</div>

      <div className="bt-row">
        <button className="bt-btn" onClick={connect} disabled={!supported}>
          i2r* 기기 검색/연결
        </button>
        <button className="bt-btn" onClick={disconnect} disabled={!server}>
          연결 해제
        </button>
      </div>

      <fieldset className="bt-fieldset">
        <legend className="bt-legend">Wi-Fi / MQTT 설정 전송</legend>

        <div className="bt-grid">
          <label>SSID</label>
          <input className="bt-input" ref={ssidRef} placeholder="SSID" defaultValue="" />

          <label>PW</label>
          <input className="bt-input" ref={pwRef} placeholder="Password" defaultValue="" />

          <label>Email</label>
          <input className="bt-input" ref={emailRef} placeholder="Email" defaultValue="" />

          <label>Broker</label>
          <input className="bt-input" ref={brokerRef} placeholder="mqtt.i2r.link" defaultValue="mqtt.i2r.link" />
        </div>

        <div className="bt-section-actions">
          <button className="bt-btn" onClick={sendWifiConfig} disabled={!characteristic}>
            📤 데이터 전송
          </button>
        </div>
      </fieldset>

      <div className="bt-device">
        <h4>선택된 기기</h4>
        <div>이름: {device?.name || '-'}</div>
      </div>
    </div>
  );
};

export default BluetoothConnect;
